import React, { useCallback, useEffect, useState } from 'react';
import { PERMISSIONS } from '../../utils/permissions';
import { api, btnPrimary, btnSecondary, card, ConfirmDialog, InlineError, input, labelCls, Modal, Skeleton, useToast } from '../ui/Kit';
import StepEditor, { RELATIONSHIPS } from './StepEditor';

// Organization → Approval workflows (approved mockup board D): templates (load into the editor as an unsaved draft —
// nothing is live until Save) · the workflow's steps (edit / remove / add, Test with a job, Save = a new version — jobs
// in approval keep theirs; Discard = back to the live version) · department workflows (new ones start as drafts) ·
// rules · "Check against previous routing" (read-only).
const TYPE = {
  start: ['START', 'bg-[#E0F2FE] text-[#075985]'], approve: ['APPROVE', 'bg-[#FEF3C7] text-[#92400E]'],
  approve_pay: ['APPROVE PAY', 'bg-[#FCE7F3] text-[#9D174D]'], notify: ['NOTIFY', 'bg-[#F1F5F9] text-[#334155]'],
  assign: ['ASSIGN', 'bg-[#DCFCE7] text-[#166534]'], recruiting: ['RECRUITING', 'bg-[#DBEAFE] text-[#1E40AF]'],
};
const FIXED = { start: 'After the form + Jessica call, the HM reviews the JD and questions and clicks Approve.',
  recruiting: 'Primary recruiter owns the job; others can schedule Alex screens and update candidates.' };
const pillCls = 'inline-flex items-center px-[9px] py-[3px] rounded-full text-[11px] font-bold tracking-[.02em] whitespace-nowrap';
const SYM = { USD: '$', EUR: '€', GBP: '£', INR: '₹' };

export function toDraft(definition) {
  const steps = (definition && definition.steps) || [];
  return { rules: { ...(definition && definition.rules) },
    steps: steps.map((s, i) => ({ ...s, parallel: i > 0 && s.group === steps[i - 1].group && ['approve', 'approve_pay', 'notify'].includes(s.type) })) };
}

export function toDefinition(draft) {
  let g = -1;
  return { rules: draft.rules, steps: draft.steps.map((s, i) => {
    if (!(s.parallel && i > 0)) g += 1;
    const { parallel, ...rest } = s;
    return { ...rest, group: g };
  }) };
}

function money(cur, n) {
  const v = Number(n || 0).toLocaleString('en-US');
  return SYM[cur] ? `${SYM[cur]}${v}` : `${cur} ${v}`;
}

export function whoText(s, opts) {
  const names = (id) => (opts.people.find((p) => p.id === id) || {}).full_name || 'someone';
  const dept = (id) => (opts.departments.find((d) => d.id === id) || {}).name || 'a department';
  const parts = (s.approvers || []).map((a) => {
    if (a.kind === 'person') return [names(a.value), 'person'];
    if (a.kind === 'relationship') return [(RELATIONSHIPS[a.value] || '').replace(/^The /, '').replace(/^Head/, 'head'), 'relationship'];
    if (a.kind === 'permission') return [`anyone with “${(PERMISSIONS.find((p) => p.key === a.value) || {}).label || a.value}”`, null];
    if (a.kind === 'department') return [`${dept(a.value)} department`, null];
    if (a.kind === 'designation') return [`anyone who is a ${a.value}`, 'legacy'];
    return ['company admins', null];
  });
  return parts;
}

export function conditionText(s, opts) {
  const out = (s.conditions || []).map((c) => {
    if (c.field === 'pay_max') return `pay max > ${Object.entries(c.thresholds || {}).map(([cur, amt]) => money(cur, amt)).join(' / ')}`;
    if (c.field === 'headcount') return c.value === 'new' ? 'new headcount' : 'replacement';
    if (c.field === 'us_citizenship') return 'US citizenship required';
    if (c.field === 'employment_type') return `employment type is ${(c.values || []).join(', ').replace(/_/g, '-')}`;
    if (c.field === 'country') return `country is ${(c.values || []).join(', ')}`;
    if (c.field === 'department') return `department is ${(c.values || []).map((id) => (opts.departments.find((d) => d.id === id) || {}).name).join(', ')}`;
    if (c.field === 'level_gte') return `level ≥ ${(opts.levels.find((lv) => lv.id === c.value) || {}).name || '?'}`;
    return c.field;
  });
  return out.join(s.match === 'any' ? ' OR ' : ' AND ');
}

function Switch({ on, onChange, label }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} onClick={() => onChange(!on)}
      className={`relative w-[38px] h-[22px] rounded-full shrink-0 mt-0.5 transition-colors ${on ? 'bg-[#0066CC]' : 'bg-[#CBD5E1]'}`}>
      <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white transition-all ${on ? 'right-[3px]' : 'left-[3px]'}`} />
    </button>
  );
}

function StepCard({ s, n, last, opts, stat, onEdit, onRemove }) {
  const [label, cls] = TYPE[s.type] || ['STEP', ''];
  const who = whoText(s, opts);
  const cond = conditionText(s, opts);
  const legacy = (s.approvers || []).some((a) => a.kind === 'designation');
  const backup = s.backup_user_id ? (opts.people.find((p) => p.id === s.backup_user_id) || {}).full_name : null;
  const meta = [];
  if (who.length && s.type !== 'start' && s.type !== 'recruiting') {
    meta.push(<span key="who">Who: {who.map(([t, k], i) => <span key={i}>{i > 0 && (s.mode === 'all' ? ' + ' : ' or ')}<b>{t}</b>{k && k !== 'legacy' ? ` (${k})` : ''}</span>)}
      {['approve', 'approve_pay'].includes(s.type) && (s.mode === 'all' ? ' (all must approve)' : who.length > 1 || (s.approvers[0] && ['permission', 'department', 'company_admins'].includes(s.approvers[0].kind)) ? ' (first to act)' : '')}
      {backup ? ` · backup: ${backup}` : ''}</span>);
  }
  if ((s.skip || {}).hm_is_dept_head) meta.push(<span key="skip">Skip if the HM is the department head</span>);
  if (s.can_edit_pay) meta.push(<span key="pay">Can edit the pay range</span>);
  if (s.type === 'notify') meta.push(<span key="fyi">No action needed — the job keeps moving</span>);
  if (s.type === 'assign') meta.push(<span key="as">{s.auto_assign ? 'Assigns the job to them directly' : 'Assigns one or more people who report to them'}</span>);
  if (s.remind_after_days) meta.push(<span key="rem">Remind after {s.remind_after_days} day{s.remind_after_days === 1 ? '' : 's'}</span>);
  if (s.escalate_after_days) meta.push(<span key="esc">Escalate after {s.escalate_after_days} day{s.escalate_after_days === 1 ? '' : 's'}</span>);
  if (stat) meta.push(<span key="avg" className="text-[#0066CC]">avg {stat.avg_days} days to decide · {stat.decisions} decision{stat.decisions === 1 ? '' : 's'}</span>);
  return (
    <div className="flex gap-3.5 items-start" data-testid={`wf-step-${s.key}`}>
      <div className="flex flex-col items-center shrink-0 w-9 self-stretch">
        <span className={`w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-sm ${cls}`}>{n}</span>
        {!last && <span aria-hidden="true" className="w-0.5 flex-1 min-h-[24px] bg-[#D9DEE8]" />}
      </div>
      <div className={`flex-1 min-w-0 border rounded-[14px] px-4 py-3.5 bg-white flex flex-col gap-2 ${last ? '' : 'mb-3'} ${legacy ? 'border-[#FDE68A]' : 'border-[#E6E9F0]'}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`${pillCls} ${cls}`}>{label}</span>
          {s.parallel && <span className="text-[11px] font-semibold text-[#64748B]">at the same time as the step above</span>}
          <b className="text-sm flex-1">{s.label}</b>
          {onEdit && <button type="button" aria-label={`Edit step ${n}`} onClick={onEdit} className="w-[34px] h-[34px] rounded-[9px] border border-[#E6E9F0] bg-white text-[#334155] inline-flex items-center justify-center">
            <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4" /></svg></button>}
          {onRemove && <button type="button" aria-label={`Remove step ${n}`} onClick={onRemove} className="w-[34px] h-[34px] rounded-[9px] border border-[#E6E9F0] bg-white text-[#B91C1C] inline-flex items-center justify-center">
            <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></svg></button>}
        </div>
        {FIXED[s.type] && <p className="text-xs text-[#64748B]">{FIXED[s.type]}</p>}
        {legacy && <p className="text-xs font-semibold text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-2.5 py-1.5" data-testid="legacy-step">Legacy routing — update this step (choose a person or a relationship)</p>}
        {cond && <div><span className="inline-flex items-center gap-1.5 px-2.5 py-[5px] rounded-lg bg-[#FFF7ED] border border-dashed border-[#FDBA74] text-[#9A3412] text-xs font-semibold">Only if: {cond}</span></div>}
        {meta.length > 0 && <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#475569]">{meta}</div>}
      </div>
    </div>
  );
}

function TestDialog({ wfId, draft, onClose }) {
  const [jobs, setJobs] = useState(null);
  const [jobId, setJobId] = useState('');
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api('/api/jobs?view=admin&segment=all&page_size=100').then((d) => setJobs(d.rows.filter((r) => r.origin === 'hm_jessica'))).catch((e) => { setError(e.message); setJobs([]); });
  }, []);
  const run = async () => {
    setError(''); setRes(null);
    try { setRes(await api(`/api/workflows/${wfId}/test`, { method: 'POST', body: { requirement_id: Number(jobId), definition: toDefinition(draft) } })); }
    catch (e) { setError(e.message); }
  };
  return (
    <Modal open title="Test with a job" onClose={onClose} width="max-w-2xl" footer={<button className={btnSecondary} onClick={onClose}>Close</button>}>
      <p className="text-sm text-[#334155]">See who each step of this workflow (including unsaved changes) would go to, and which steps skip. Nothing is sent.</p>
      <div className="flex gap-2 mt-3 items-end">
        <label className="block flex-1"><span className={labelCls}>Job</span>
          <select className={input} value={jobId} onChange={(e) => setJobId(e.target.value)} disabled={!jobs}>
            <option value="">{jobs === null ? 'Loading jobs…' : 'Choose a job…'}</option>
            {(jobs || []).map((j) => <option key={j.id} value={j.id}>{j.req_id ? `${j.req_id} · ` : ''}{j.job_title}</option>)}
          </select></label>
        <button className={btnPrimary} disabled={!jobId} onClick={run}>Test</button>
      </div>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
      {res && (
        <ol className="mt-3 divide-y divide-[#EEF1F6] border border-[#E6E9F0] rounded-lg" data-testid="test-result">
          {res.steps.map((s) => (
            <li key={s.key} className="px-3 py-2.5 text-sm flex gap-3">
              <span className={`${pillCls} ${(TYPE[s.type] || [])[1]} self-start`}>{(TYPE[s.type] || [])[0]}</span>
              <div className="min-w-0"><p className="font-semibold">{s.label}</p>
                <p className="text-xs text-[#64748B]">{!s.applies ? `Skipped — ${s.skip_reason}` :
                  s.type === 'assign' ? (s.assign && s.assign.name ? `${s.assign.mode === 'auto' ? 'Assigned directly to' : 'Goes to'} ${s.assign.name}${s.assign.reason ? ` (${s.assign.reason})` : ''}` : s.assign && s.assign.mode === 'designation' ? `Anyone who is a ${s.assign.designation}` : 'Company admins assign') :
                  ['start', 'recruiting'].includes(s.type) ? '—' :
                  s.approvers.map((a) => a.names.join(', ') + (a.fallback_reason ? ` — no approver found (${a.fallback_reason}), company admins` : '')).join(' + ')}</p>
                {s.notes.map((x) => <p key={x} className="text-xs text-[#92400E]">{x}</p>)}</div>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}

function CheckDialog({ onClose }) {
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api('/api/workflows/check-routing').then(setRes).catch((e) => setError(e.message)); }, []);
  const tone = { ok: ['Same', 'bg-[#DCFCE7] text-[#166534]'], warn: ['Improved', 'bg-[#FEF3C7] text-[#92400E]'], diff: ['Different', 'bg-[#FEE2E2] text-[#991B1B]'] };
  const fmt = (x) => `${x.steps.map((s) => `${s.step}: ${s.people.join(', ') || '—'}`).join(' · ') || 'no approvals'} → ${x.assign}`;
  return (
    <Modal open title="Check against previous routing" onClose={onClose} width="max-w-3xl" footer={<button className={btnSecondary} onClick={onClose}>Close</button>}>
      <InlineError>{error}</InlineError>
      {!res && !error && <Skeleton className="h-24 w-full" />}
      {res && (
        <div className="space-y-3" data-testid="check-result">
          <p className="text-sm text-[#334155]">Read-only: for every job, the chain the routing set up before workflows would give today, next to the chain your default workflow gives. Nothing is changed.</p>
          {res.workflow && res.workflow.edited
            ? <p className="text-sm bg-[#F1F5F9] rounded-lg px-3 py-2">Your default workflow <b>{res.workflow.name}</b> has been edited, so differences from the previous routing are expected.</p>
            : res.workflow && <p className={`text-sm rounded-lg px-3 py-2 ${res.routing_match ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#FEE2E2] text-[#991B1B]'}`}>
              {res.routing_match ? `Current routing (version ${res.workflow.version_no}) matches the previous routing settings exactly.` : 'Current routing differs from the previous routing settings.'}</p>}
          <p className="text-sm font-semibold">{res.summary.jobs} jobs checked · {res.summary.differences} different · {res.summary.warnings} improved</p>
          <ul className="divide-y divide-[#EEF1F6] border border-[#E6E9F0] rounded-lg max-h-[50vh] overflow-y-auto">
            {res.jobs.map((j) => (
              <li key={j.requirement_id} className="px-3 py-2.5 text-sm">
                <div className="flex gap-2 items-center"><span className={`${pillCls} ${tone[j.status][1]}`}>{tone[j.status][0]}</span><b>{j.req_id ? `${j.req_id} · ` : ''}{j.job_title}</b></div>
                {j.status === 'ok' ? <p className="text-xs text-[#64748B] mt-0.5">{fmt(j.new)}</p> : (
                  <><p className="text-xs text-[#64748B] mt-0.5">Before: {fmt(j.old)}</p><p className="text-xs text-[#0F172A]">Now: {fmt(j.new)}</p>
                    {j.status === 'warn' && <p className="text-xs text-[#92400E]">The previous routing named someone who is no longer active — the step now goes to company admins.</p>}</>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}

function AddDeptDialog({ departments, templates, onClose, onCreated }) {
  const [dep, setDep] = useState('');
  const [from, setFrom] = useState('copy');
  const [error, setError] = useState('');
  const create = async () => {
    setError('');
    try { const d = await api('/api/workflows', { method: 'POST', body: { department_id: Number(dep), template_key: from } }); onCreated(d); }
    catch (e) { setError(e.message); }
  };
  return (
    <Modal open title="Department workflow" onClose={onClose}
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button><button className={btnPrimary} disabled={!dep} onClick={create}>Create</button></>}>
      <p className="text-sm text-[#334155]">Jobs in this department use its own workflow instead of the default.</p>
      <label className="block mt-3"><span className={labelCls}>Department</span>
        <select className={input} value={dep} onChange={(e) => setDep(e.target.value)}>
          <option value="">Choose…</option>{departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></label>
      <label className="block mt-3"><span className={labelCls}>Start from</span>
        <select className={input} value={from} onChange={(e) => setFrom(e.target.value)}>
          <option value="copy">A copy of the default workflow</option>
          {templates.map((t) => <option key={t.key} value={t.key}>{t.name} template</option>)}</select></label>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

const PARALLEL_OK = ['approve', 'approve_pay', 'notify'];
// "at the same time as the step above" only after another approval / notify step
const fixParallel = (arr) => arr.map((x, j) => ({ ...x, parallel: !!x.parallel && j > 0 && PARALLEL_OK.includes(x.type) && PARALLEL_OK.includes(arr[j - 1].type) }));

const newStep = (n) => ({ key: `step${n}`, type: 'approve', label: 'Approval', approvers: [{ kind: 'relationship', value: 'dept_head' }], mode: 'any', conditions: [],
  match: 'all', skip: {}, remind_after_days: 2, escalate_after_days: null, can_edit_pay: false, backup_user_id: null, auto_assign: false, parallel: false });

export default function WorkflowBuilder({ onOpenSetup }) {
  const toast = useToast();
  const [draftFrom, setDraftFrom] = useState(null);
  const [problems, setProblems] = useState(null);
  const [list, setList] = useState(null);
  const [opts, setOpts] = useState({ people: [], departments: [], levels: [] });
  const [wfId, setWfId] = useState(null);
  const [wf, setWf] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [edit, setEdit] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const loadList = useCallback(async () => {
    const [l, s] = await Promise.all([api('/api/workflows'), api('/api/org/setup')]);
    setList(l); setOpts({ people: s.people || [], departments: s.departments || [], levels: s.levels || [] });
    return l;
  }, []);
  const open = useCallback(async (id) => {
    setError(''); setProblems(null); setDraftFrom(null);
    const d = await api(`/api/workflows/${id}`);
    setWfId(id); setWf(d); setDraft(toDraft(d.definition)); setDirty(false);
  }, []);
  useEffect(() => {
    loadList().then((l) => open((l.workflows.find((w) => w.scope === 'default') || l.workflows[0]).id)).catch((e) => setError(e.message));
  }, [loadList, open]);
  if (!list || !draft) return error ? <InlineError>{error}</InlineError> : <Skeleton className="h-64 w-full" />;
  const change = (d) => { setDraft(d); setDirty(true); };
  const steps = draft.steps;
  const setRule = (k, v) => change({ ...draft, rules: { ...draft.rules, [k]: v } });
  const save = async () => {
    setError(''); setProblems(null); setBusy(true);
    try {
      const d = await api(`/api/workflows/${wfId}`, { method: 'PUT', body: { definition: toDefinition(draft), ...(draftFrom ? { template_key: draftFrom.key } : {}) } });
      setWf(d); setDraft(toDraft(d.definition)); setDirty(false); setDraftFrom(null);
      toast.show(wf.is_draft ? `${d.name} is live — version ${d.version_no}` : `Saved — version ${d.version_no}`); loadList();
    } catch (e) { setError(e.message); setProblems(e.problems); }
    setBusy(false);
  };
  const loadTemplate = async (t) => {
    setError(''); setProblems(null);
    try { const d = await api(`/api/workflows/templates/${t.key}`); setDraft(toDraft(d.definition)); setDraftFrom({ key: t.key, name: t.name }); setDirty(true); }
    catch (e) { setError(e.message); }
  };
  const discard = async () => {
    if (wf.is_draft) { setDialog({ kind: 'remove' }); return; }
    await open(wfId);
  };
  const remove = async () => {
    setDialog(null);
    try {
      await api(`/api/workflows/${wfId}`, { method: 'DELETE' });
      toast.show(wf.is_draft ? 'Draft discarded' : 'Department workflow removed — new jobs use the default');
      const l = await loadList(); open(l.workflows.find((w) => w.scope === 'default').id);
    } catch (e) { setError(e.message); }
  };
  const saveStep = (s) => {
    const next = [...steps];
    if (edit.index == null) { const at = next.findIndex((x) => x.type === 'assign'); next.splice(at, 0, s); } else next[edit.index] = s;
    change({ ...draft, steps: fixParallel(next) }); setEdit(null);
  };
  const isDefault = wf.scope === 'default';
  const deptWorkflows = list.workflows.filter((w) => w.scope === 'department');
  const freeDepts = opts.departments.filter((d) => !deptWorkflows.some((w) => w.department_id === d.id));
  return (
    <div className="space-y-5" data-testid="workflow-builder">
      <section>
        <h2 className="text-[17px] font-bold mb-1">Start from a template</h2>
        <p className="text-[13px] text-[#64748B] mb-3">Pick the one closest to how your company hires, then adjust the steps below.</p>
        <div className="grid gap-3.5 grid-cols-1 md:grid-cols-3">
          {list.templates.map((t) => {
            const inUse = wf.template_key === t.key && !wf.is_draft;
            const drafting = draftFrom && draftFrom.key === t.key;
            return (
              <button key={t.key} type="button" onClick={() => loadTemplate(t)} aria-pressed={inUse}
                className={`text-left flex flex-col gap-2.5 px-[18px] py-4 rounded-[14px] border-[1.5px] bg-white ${inUse ? 'border-[#0066CC] bg-[#F5F9FF] shadow-[0_0_0_3px_rgba(0,102,204,.12)]' : 'border-[#E6E9F0] hover:border-[#9DBFE6]'}`}>
                <div className="flex items-center"><b className="flex-1 text-[15px]">{t.name}</b>
                  {drafting ? <span className={`${pillCls} bg-[#FEF3C7] text-[#92400E]`}>DRAFT</span> : inUse ? <span className={`${pillCls} bg-[#0066CC] text-white`}>IN USE</span> : t.suggested ? <span className={`${pillCls} bg-[#E0F2FE] text-[#075985]`}>SUGGESTED</span> : <span className="text-xs text-[#64748B]">{t.size}</span>}</div>
                <p className="text-xs text-[#64748B]">{t.description}</p>
                <div className="flex flex-wrap gap-1 items-center">{t.chain.split(' → ').map((x, i) => (
                  <React.Fragment key={x}>{i > 0 && <span className="text-[11px] text-[#64748B]">→</span>}<span className="px-2 py-[3px] rounded-[7px] bg-[#F1F5F9] text-[11px] font-semibold text-[#334155]">{x}</span></React.Fragment>))}</div>
              </button>
            );
          })}
        </div>
        {wf.template_key === 'current_routing' && <p className="text-xs text-[#64748B] mt-2">In use now: <b>Current routing</b> — the routing your company had before approval workflows.</p>}
      </section>

      <div className="flex gap-5 flex-col xl:flex-row items-start">
        <section className={card + ' rounded-[16px] px-[22px] py-5 flex-1 min-w-0 w-full'}>
          <div className="flex items-center gap-2.5 flex-wrap mb-[18px]">
            <div className="flex-1 min-w-[220px]"><h2 className="text-[17px] font-bold m-0">{isDefault ? 'Default workflow' : wf.name}</h2>
              <p className="text-[13px] text-[#64748B] mt-0.5">{isDefault ? 'Applies to every department without its own workflow.' : wf.is_draft ? `Not live yet — jobs in ${wf.department || 'this department'} use the default until you Save.` : `Jobs in ${wf.department || 'this department'} use this workflow.`}{wf.is_draft ? ' Draft' : ` Version ${wf.version_no}`}{dirty ? ' · unsaved changes' : ''}</p></div>
            {isDefault && <button className={btnSecondary + ' !py-1.5'} onClick={() => setDialog({ kind: 'check' })}>Check against previous routing</button>}
            {!isDefault && !wf.is_draft && <button className={btnSecondary + ' !py-1.5 !text-[#B91C1C]'} onClick={() => setDialog({ kind: 'remove' })}>Remove</button>}
            <button className={btnSecondary + ' !py-1.5'} onClick={() => setDialog({ kind: 'test' })}>Test with a job</button>
            <button className={btnPrimary + ' !py-1.5'} disabled={busy || !(dirty || wf.is_draft)} onClick={save}>{wf.is_draft ? 'Save and make live' : 'Save workflow'}</button>
          </div>
          {(draftFrom || wf.is_draft || dirty) && (
            <div role="status" data-testid="draft-banner" className="mb-4 flex flex-wrap items-center gap-3 rounded-[12px] border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#78350F]">
              <p className="flex-1 min-w-[240px] m-0">{draftFrom ? <><b>Draft from {draftFrom.name}</b> — review, Test with a job, then Save to publish. The live version stays in use until then.</>
                : wf.is_draft ? <><b>Draft</b>{wf.draft_note ? ` — ${wf.draft_note.replace(/^Draft from /, 'from ')}` : ''}. Review, Test with a job, then Save to make it live.</>
                : <><b>Unsaved changes</b> — the live version (version {wf.version_no}) stays in use until you Save.</>}</p>
              <button type="button" className={btnSecondary + ' !py-1'} onClick={discard}>Discard</button>
            </div>
          )}
          {error && <div className="mb-3"><InlineError>{error}</InlineError>
            {problems && problems.length > 0 && (
              <div className="mt-2 rounded-lg border border-[#FECACA] bg-[#FEF2F2] px-3 py-2.5 text-sm text-[#7F1D1D]" data-testid="save-problems">
                <p className="font-semibold">These steps can’t find an approver for the sample job:</p>
                <ul className="list-disc ml-5 mt-1">{problems.map((p) => <li key={p.step + p.problem}><b>{p.label}</b> — {p.problem}</li>)}</ul>
                <button type="button" className="mt-1.5 font-semibold text-[#0066CC] underline" onClick={() => (onOpenSetup ? onOpenSetup() : window.location.assign('/organization?tab=setup'))}>
                  Set department heads, managers and people in Org setup →</button>
              </div>
            )}</div>}
          {steps.map((s, i) => (
            <StepCard key={s.key} s={s} n={i + 1} last={i === steps.length - 1} opts={opts} stat={(wf.stats || {})[s.key]}
              onEdit={['start', 'recruiting'].includes(s.type) ? null : () => setEdit({ index: i, step: s })}
              onRemove={['approve', 'approve_pay', 'notify'].includes(s.type) ? () => change({ ...draft, steps: fixParallel(steps.filter((_, j) => j !== i)) }) : null} />
          ))}
          <div className="pt-3.5 pl-[50px]">
            <button className={btnSecondary + ' !py-1.5'} disabled={steps.length >= 10} title={steps.length >= 10 ? 'A workflow has at most 10 steps' : undefined}
              onClick={() => setEdit({ index: null, step: newStep(steps.length + 1) })}>+ Add step</button>
          </div>
        </section>

        <div className="flex flex-col gap-5 w-full xl:w-[340px] shrink-0">
          <section className={card + ' rounded-[16px] px-[22px] py-5'} data-testid="dept-workflows">
            <div className="flex items-center mb-1"><h2 className="text-[17px] font-bold m-0 flex-1">Department workflows</h2>
              <button type="button" className="text-[13px] font-semibold text-[#0066CC] disabled:opacity-40" disabled={!freeDepts.length} onClick={() => setDialog({ kind: 'dept' })}>+ Add</button></div>
            <p className="text-[13px] text-[#64748B] mb-1.5">Override the default for specific departments.</p>
            {!isDefault && <button type="button" className="text-xs font-semibold text-[#0066CC] mb-1" onClick={() => open(list.workflows.find((w) => w.scope === 'default').id)}>← Back to the default workflow</button>}
            {opts.departments.map((d) => {
              const w = deptWorkflows.find((x) => x.department_id === d.id);
              return (
                <button key={d.id} type="button" disabled={!w} onClick={() => w && open(w.id)}
                  className={`w-full text-left flex items-center gap-2.5 py-2.5 border-t border-[#EEF1F6] ${w && w.id === wfId ? 'bg-[#F5F9FF] -mx-2 px-2 rounded' : ''}`}>
                  <div className="flex-1 min-w-0"><p className="font-semibold text-sm">{d.name}</p><p className="text-xs text-[#64748B] truncate">{w ? (w.is_draft ? 'Draft — not live yet' : w.chain) : 'Uses default'}</p></div>
                  <span className={`${pillCls} ${w ? (w.is_draft ? 'bg-[#FEF3C7] text-[#92400E]' : 'bg-[#E0E7FF] text-[#3730A3]') : 'bg-[#F1F5F9] text-[#334155]'}`}>{w ? (w.is_draft ? 'DRAFT' : 'CUSTOM') : 'DEFAULT'}</span>
                </button>
              );
            })}
            {!opts.departments.length && <p className="text-xs text-[#64748B] border-t border-[#EEF1F6] pt-2.5">Add departments in Org setup to give them their own workflow.</p>}
          </section>
          <section className={card + ' rounded-[16px] px-[22px] py-5'} data-testid="wf-rules">
            <h2 className="text-[17px] font-bold mb-1.5">Rules</h2>
            {[['send_back_comment', 'Send back needs a comment', 'The job returns to the HM with the reason.'],
              ['notify_hm_each_step', 'Notify the HM at every step', 'Bell + email when a step is approved.']].map(([k, t, h]) => (
              <div key={k} className="flex gap-3 items-start py-2.5 border-t border-[#EEF1F6]"><Switch on={draft.rules[k]} label={t} onChange={(v) => setRule(k, v)} />
                <div><p className="text-sm font-semibold">{t}</p><p className="text-xs text-[#64748B]">{h}</p></div></div>
            ))}
            <div className="flex gap-3 items-start py-2.5 border-t border-[#EEF1F6]"><Switch on={draft.rules.restart_on === 'title_or_pay'} label="Edits after approval restart the chain" onChange={(v) => setRule('restart_on', v ? 'title_or_pay' : 'pay')} />
              <div><p className="text-sm font-semibold">Edits after approval restart the chain</p><p className="text-xs text-[#64748B]">{draft.rules.restart_on === 'title_or_pay' ? 'On: a title or pay change goes back through approval.' : 'Off: only pay changes restart it.'}</p></div></div>
            <div className="flex gap-3 items-start py-2.5 border-t border-[#EEF1F6]"><Switch on={!!draft.rules.escalate_after_days} label="Escalate" onChange={(v) => setRule('escalate_after_days', v ? 3 : null)} />
              <div><p className="text-sm font-semibold">Escalate after {draft.rules.escalate_after_days ? (
                <input aria-label="Escalate after days" className={input + ' !w-14 !py-0.5 !px-1.5 inline-block'} inputMode="numeric" value={draft.rules.escalate_after_days}
                  onChange={(e) => setRule('escalate_after_days', Number(e.target.value.replace(/\D/g, '')) || 1)} />) : 'N'} days</p>
                <p className="text-xs text-[#64748B]">Goes to the approver’s manager (company admins if none). Business days, company time zone.</p></div></div>
          </section>
        </div>
      </div>

      {edit && <StepEditor step={edit.step} isNew={edit.index == null} opts={opts} onClose={() => setEdit(null)} onSave={saveStep}
        canParallel={edit.index == null ? ['approve', 'approve_pay', 'notify'].includes((steps[steps.findIndex((x) => x.type === 'assign') - 1] || {}).type) : edit.index > 0 && ['approve', 'approve_pay', 'notify'].includes(steps[edit.index - 1].type)} />}
      {dialog && dialog.kind === 'test' && <TestDialog wfId={wfId} draft={draft} onClose={() => setDialog(null)} />}
      {dialog && dialog.kind === 'check' && <CheckDialog onClose={() => setDialog(null)} />}
      {dialog && dialog.kind === 'dept' && <AddDeptDialog departments={freeDepts} templates={list.templates} onClose={() => setDialog(null)}
        onCreated={async (d) => { setDialog(null); toast.show(`${d.name}: draft created — Save to make it live`); await loadList(); open(d.id); }} />}
      <ConfirmDialog open={!!dialog && dialog.kind === 'remove'} title={wf.is_draft ? `Discard the ${wf.name} draft?` : `Remove ${wf.name}?`} danger
        message={wf.is_draft ? 'It was never live — jobs in this department keep using the default workflow.' : 'New jobs in this department use the default workflow. Jobs already in approval keep this one.'}
        confirmLabel={wf.is_draft ? 'Discard draft' : 'Remove'} onConfirm={remove} onClose={() => setDialog(null)} />
    </div>
  );
}
